import React, { useEffect, useState } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { createSupportTicket } from '../../api/support';
import { getAllCampaigns } from '../../api/campaign';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdCloudUpload, MdClose } from 'react-icons/md';

const CreateSupportTicket = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [formData, setFormData] = useState({
    campaign: '',
    issueType: '',
    priority: '',
    description: '',
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setHeaderTitle('Support Tickets');
    fetchCampaigns();
  }, [setHeaderTitle]);

  const fetchCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      const data = await getAllCampaigns();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const issueTypes = [
    'Technical Issue',
    'Campaign Related',
    'Account Issue',
    'Payment Issue',
    'General Inquiry',
    'Other'
  ];

  const priorities = [
    'Low',
    'Medium',
    'High',
    'Urgent'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.issueType) {
      newErrors.issueType = 'Issue type is required';
    }

    if (!formData.priority) {
      newErrors.priority = 'Priority is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const requestData = {
        ...formData,
        message: formData.description,
        image: image || '',
      };
      await createSupportTicket(requestData);
      toast.success('Support ticket created successfully!');
      setTimeout(() => {
        navigate('/support');
      }, 1500);
    } catch (error) {
      console.error('Error creating support ticket:', error);
      toast.error('Failed to create support ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      campaign: '',
      issueType: '',
      priority: '',
      description: '',
    });
    setImage(null);
    setImagePreview(null);
    setErrors({});
  };

  return (
    <div className='w-full'>
      <section className='bg-white rounded-md shadow-sm p-6'>
        <form onSubmit={handleSubmit}>
          <div className='flex gap-6'>
            {/* Left Column - Form Fields */}
            <div className='flex-1'>
              {/* Select Campaign */}
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  <span className='text-red-500'>*</span> Select Campaign
                </label>
                <select
                  name='campaign'
                  value={formData.campaign}
                  onChange={handleInputChange}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
                  disabled={campaignsLoading}
                >
                  <option value=''>Select Type of Campaign</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign._id || campaign.id} value={campaign._id || campaign.id}>
                      {campaign.title || campaign.campaignTitle}
                    </option>
                  ))}
                </select>
              </div>

              {/* Issue Type and Priority Row */}
              <div className='grid grid-cols-2 gap-4 mb-4'>
                {/* Issue Type */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    <span className='text-red-500'>*</span> Issue Type
                  </label>
                  <select
                    name='issueType'
                    value={formData.issueType}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${
                      errors.issueType ? 'border-red-500' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  >
                    <option value=''>Select Issue Type</option>
                    {issueTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.issueType && (
                    <p className='text-red-500 text-sm mt-1'>{errors.issueType}</p>
                  )}
                </div>

                {/* Priority */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    <span className='text-red-500'>*</span> Priority
                  </label>
                  <select
                    name='priority'
                    value={formData.priority}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${
                      errors.priority ? 'border-red-500' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  >
                    <option value=''>Select Priority</option>
                    {priorities.map((priority) => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                  {errors.priority && (
                    <p className='text-red-500 text-sm mt-1'>{errors.priority}</p>
                  )}
                </div>
              </div>

              {/* Describe Issue */}
              <div className='mb-4'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  <span className='text-red-500'>*</span> Describe Issue
                </label>
                <textarea
                  name='description'
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder='Describe the issue.'
                  rows={6}
                  className={`w-full px-3 py-2 border ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none`}
                />
                {errors.description && (
                  <p className='text-red-500 text-sm mt-1'>{errors.description}</p>
                )}
              </div>
            </div>

            {/* Right Column - Image Upload */}
            <div className='w-64'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Image
              </label>
              <div className='border-2 border-dashed border-gray-300 rounded-lg p-4 text-center h-48 flex flex-col items-center justify-center'>
                {imagePreview ? (
                  <div className='relative w-full h-full'>
                    <img
                      src={imagePreview}
                      alt='Preview'
                      className='w-full h-full object-contain rounded'
                    />
                    <button
                      type='button'
                      onClick={removeImage}
                      className='absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600'
                    >
                      <MdClose size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <MdCloudUpload className='text-4xl text-gray-400 mb-2' />
                    <p className='text-sm text-gray-500 mb-2'>Upload Media Here</p>
                    <label className='cursor-pointer'>
                      <span className='px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50'>
                        Choose File
                      </span>
                      <input
                        type='file'
                        accept='image/*'
                        onChange={handleImageChange}
                        className='hidden'
                      />
                    </label>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex items-center justify-between mt-6 pt-4 border-t border-gray-200'>
            <button
              type='button'
              onClick={handleReset}
              className='px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium'
              disabled={loading}
            >
              Reset
            </button>
            <button
              type='submit'
              className='px-6 py-2 bg-primary text-white rounded-md hover:bg-primaryHoverDark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Request'}
            </button>
          </div>
        </form>
      </section>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default CreateSupportTicket;
